import type { ReactNode } from "react";
import type { PatientProfile } from "../../api/clinicalWorkspace";
import { DemographicsEditor } from "../../components/patient-profile/DemographicsEditor";
import {
  AllergiesEditor,
  FamilyHistoryEditor,
  ImmunizationsEditor,
  MedicationsEditor,
  PmhEditor,
  SocialHistoryEditor,
} from "../../components/patient-profile/sectionEditors";
import {
  AllergiesBody,
  DemographicsBody,
  FamilyHistoryBody,
  ImmunizationsBody,
  MedicationsBody,
  PlaceholderBody,
  PmhBody,
  SocialHistoryBody,
} from "./ProfileSectionPanels";
import type { ProfileSection } from "./profileSections";
import type { SectionEditing } from "./useSectionEditing";

/**
 * One record, two views: the profile page and the encounter's Health profile
 * panel render the same read body or editor for a section, chosen by the
 * shared edit session. Placeholders and the Visits section are page concerns
 * and never reach this switch.
 */
export function ProfileSectionBody({
  section,
  profile,
  loadError,
  editing,
  after,
  addNew = false,
}: {
  readonly section: ProfileSection;
  readonly profile: PatientProfile | undefined;
  /** True when the profile request failed (the body says so instead of "Loading…"). */
  readonly loadError: boolean;
  readonly editing: SectionEditing;
  /** Rendered under the read body only (never inside an editor). */
  readonly after?: ReactNode;
  readonly addNew?: boolean;
}) {
  if (profile === undefined) {
    return (
      <p className="pp-quiet" role="status">
        {loadError ? "This section could not be loaded." : "Loading…"}
      </p>
    );
  }
  if (editing.editing === section.key) {
    // Every editor receives the same snapshot its write token describes,
    // including demographic diffs and the family-history narrative.
    const editorProfile = editing.editProfile ?? profile;
    const shared = {
      resetKey: editing.resetKey,
      saving: editing.saving,
      serverError: editing.error,
      onCancel: editing.cancel,
      onDirtyChange: editing.setDirty,
      startWithNewRow: addNew,
      ...(editing.conflicted ? { onReload: () => void editing.reload() } : {}),
    };
    switch (section.key) {
      case "demographics":
        return (
          <DemographicsEditor
            demographics={editorProfile.demographics}
            {...shared}
            onSave={(input) => void editing.saveDemographics(input)}
          />
        );
      case "pmh":
        return (
          <PmhEditor
            items={editorProfile.pmh ?? []}
            {...shared}
            onSave={(pmh) => void editing.saveSection({ pmh })}
          />
        );
      case "familyHistory":
        return (
          <FamilyHistoryEditor
            section={editorProfile.familyHistory ?? { narrative: "", rows: [] }}
            {...shared}
            onSave={(familyHistory) => void editing.saveSection({ familyHistory })}
          />
        );
      case "socialHistory":
        return (
          <SocialHistoryEditor
            items={(editorProfile.socialHistory ?? []).filter(
              (item) => item.clinicalStatus === "active",
            )}
            {...shared}
            onSave={(socialHistory) => void editing.saveSection({ socialHistory })}
          />
        );
      case "allergies":
        return (
          <AllergiesEditor
            items={(editorProfile.allergies ?? []).filter((item) => item.status !== "inactive")}
            {...shared}
            onSave={(allergies) => void editing.saveSection({ allergies })}
          />
        );
      case "immunizations":
        return (
          <ImmunizationsEditor
            items={(editorProfile.immunizations ?? []).filter(
              (item) => item.status !== "entered_in_error",
            )}
            {...shared}
            onSave={(immunizations) => void editing.saveSection({ immunizations })}
          />
        );
      case "medications":
        return (
          <MedicationsEditor
            items={editorProfile.medications ?? []}
            {...shared}
            onSave={(medications) => void editing.saveSection({ medications })}
          />
        );
      default:
        return <PlaceholderBody section={section} />;
    }
  }
  const saved =
    editing.savedSection === section.key ? (
      <p className="pp-saved" role="status">
        Saved.
      </p>
    ) : null;
  const body = () => {
    switch (section.key) {
      case "demographics":
        return <DemographicsBody demographics={profile.demographics} />;
      case "pmh":
        return <PmhBody items={profile.pmh ?? []} />;
      case "familyHistory":
        return <FamilyHistoryBody section={profile.familyHistory ?? { narrative: "", rows: [] }} />;
      case "socialHistory":
        return <SocialHistoryBody items={profile.socialHistory ?? []} />;
      case "allergies":
        return <AllergiesBody items={profile.allergies ?? []} />;
      case "immunizations":
        return <ImmunizationsBody items={profile.immunizations ?? []} />;
      case "medications":
        return <MedicationsBody items={profile.medications ?? []} />;
      default:
        return <PlaceholderBody section={section} />;
    }
  };
  return (
    <>
      {saved}
      {body()}
      {after}
    </>
  );
}

/**
 * Edit is offered exactly where the record allows it: identity sections for
 * every role that can open the profile, clinical sections only when the
 * server projected them in. The server stays authoritative on the write.
 */
/**
 * Sections that are a view of something recorded elsewhere. Offering Add or
 * Edit here would raise a dialog that cannot save: a result belongs to the
 * laboratory that issued it, a document to the replacement workflow, and an
 * imported visit to the clinic that held it.
 */
const READ_ONLY_SECTION_KEYS: ReadonlySet<ProfileSection["key"]> = new Set([
  "overview",
  "visits",
  "documents",
  "letters",
  "labs",
  "imaging",
  "consults",
]);

export function sectionIsEditable(
  section: ProfileSection,
  profile: PatientProfile | undefined,
): boolean {
  return (
    profile !== undefined &&
    !section.placeholder &&
    !READ_ONLY_SECTION_KEYS.has(section.key) &&
    (section.contractKey === undefined || profile[section.contractKey] !== undefined)
  );
}
